// Sole reader of basket.appliedPromotions[] for reward-bundle detection.
// Consumers call the exported helpers; DTO shape changes stay contained here.

const APPLIED_PROMOTIONS_KEY = 'appliedPromotions';
const PROMOTION_TYPE_KEY = 'promotionType';
const PROMOTION_ID_KEY = 'promotionId';
const LOYALTY_DISCOUNT_POINTS_KEY = 'loyaltyDiscountPoints';
const DISPLAY_NAME_KEY = 'displayName';
const LIST_PRICE_SUBTOTAL_KEY = 'listPriceSubtotal';
const ITEMS_KEY = 'items';

const BUNDLE_PROMOTION_TYPE = 'REWARD_BUNDLE';
const BUNDLE_ID_PREFIX = 'RB_';
// REWARD_GROUPS.POINTS_100 is not available on this branch (BEP-1470 was cut
// before RBF-3/BEP-1463 landed) -- use the raw string until a rebase pulls
// the shared constant in.
const POINTS_SUFFIX = ' Points';

// --- Detection helper (internal) ---
function isBundlePromotion(promo) {
    // PRIMARY: promotionType enum (backend-owned, strongly typed)
    if (promo[PROMOTION_TYPE_KEY] === BUNDLE_PROMOTION_TYPE) {
        return true;
    }

    // FALLBACK: promotionId prefix (defense-in-depth)
    const promotionId = promo[PROMOTION_ID_KEY];

    return typeof promotionId === 'string' && promotionId.toUpperCase().startsWith(BUNDLE_ID_PREFIX);
}

function getAppliedPromotions(basketRoot) {
    return basketRoot?.[APPLIED_PROMOTIONS_KEY];
}

/**
 * isBundleApplied(basketRoot) -> boolean
 * @param basketRoot - either basket or basket.pickupBasket
 */
function isBundleApplied(basketRoot) {
    const promotions = getAppliedPromotions(basketRoot);

    if (!promotions || promotions.length === 0) {
        return false;
    }

    return promotions.some(isBundlePromotion);
}

/**
 * getBundlePromotion(basketRoot) -> object | null
 * Returns { promotionId, promotionType, loyaltyDiscountPoints, displayName }
 */
function getBundlePromotion(basketRoot) {
    const promotions = getAppliedPromotions(basketRoot);

    if (!promotions || promotions.length === 0) {
        return null;
    }

    const promo = promotions.find(isBundlePromotion);

    if (!promo) {
        return null;
    }

    return {
        promotionId: promo[PROMOTION_ID_KEY],
        promotionType: promo[PROMOTION_TYPE_KEY],
        loyaltyDiscountPoints: promo[LOYALTY_DISCOUNT_POINTS_KEY],
        displayName: promo[DISPLAY_NAME_KEY]
    };
}

/**
 * getBundleDisqualification(basketRoot) -> null
 * OI-02a PLACEHOLDER: Returns null until Cart team provides a
 * disqualification-scenario contract sample. Consumers wire to this now;
 * when the shape is confirmed, only this function changes.
 */
// eslint-disable-next-line no-unused-vars
function getBundleDisqualification(basketRoot) {
    return null;
}

/**
 * getBundleOrderSummary(basketRoot) -> object | null
 * OI-02b resolution (Option A, TADR-RBF10-001): client-side derivation of
 * points totals from item-level data, since the Cart DTO does not (yet)
 * surface `totalPointsCost`/`netPointsCost` directly.
 *
 * Derivation:
 *   grossPointsCost = SUM of item.listPriceSubtotal where the value ends in
 *                     " Points" (parses "100 Points" -> 100). listPriceSubtotal
 *                     is already extended (line total), so no qty multiplication.
 *   loyaltyDiscountPoints = promo.loyaltyDiscountPoints from the bundle entry
 *   netPointsCost = grossPointsCost - loyaltyDiscountPoints
 *   promoLine = null (consumer builds the localized "3 for {N} Points
 *               Applied" string via its own localization resources)
 *
 * Returns null if grossPointsCost cannot be parsed to a positive value —
 * guards against locale/format drift (lowercase "points", thousands
 * separator) silently rendering "~~0~~ -50" in the UI.
 */
function getBundleOrderSummary(basketRoot) {
    const bundlePromo = getBundlePromotion(basketRoot);

    if (!bundlePromo) {
        return null;
    }

    const items = basketRoot?.[ITEMS_KEY] || [];

    const grossPointsCost = items.reduce((total, item) => {
        const subtotal = item[LIST_PRICE_SUBTOTAL_KEY];

        if (typeof subtotal === 'string' && subtotal.endsWith(POINTS_SUFFIX)) {
            const parsed = parseInt(subtotal, 10);

            if (!isNaN(parsed)) {
                return total + parsed;
            }
        }

        return total;
    }, 0);

    if (grossPointsCost <= 0) {
        return null;
    }

    const loyaltyDiscountPoints = bundlePromo.loyaltyDiscountPoints || 0;
    const netPointsCost = grossPointsCost - loyaltyDiscountPoints;

    if (netPointsCost < 0) {
        return null;
    }

    return {
        grossPointsCost,
        netPointsCost,
        loyaltyDiscountPoints,
        promoLine: null
    };
}

export {
    isBundleApplied, getBundlePromotion, getBundleDisqualification, getBundleOrderSummary
};
